/**
 * Device Fingerprinting for Fraud Detection
 *
 * Generates a unique fingerprint based on browser and device characteristics
 * Used to detect duplicate Free SIM orders from the same device
 */

export interface DeviceInfo {
  screenResolution: string
  colorDepth: number
  timezone: string
  language: string
  platform: string
  cookieEnabled: boolean
  doNotTrack: string | null
  hardwareConcurrency: number
  deviceMemory?: number
  maxTouchPoints: number
  timestamp: string
}

/**
 * Get browser cookie by name
 */
export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`
  const parts = value.split(`; ${name}=`)
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null
  return null
}

/**
 * Set browser cookie
 */
export function setCookie(name: string, value: string, days: number = 730): void {
  const expires = new Date()
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000)
  document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`
}

/**
 * Get or create a tracking cookie ID for Free SIM fraud detection
 */
export function getOrCreateTrackingCookie(): string {
  const cookieName = 'silux_freesim_visitor'
  let cookieId = getCookie(cookieName)

  if (!cookieId) {
    // Generate a new UUID-like tracking ID
    cookieId = crypto.randomUUID ? crypto.randomUUID() : generateUUID()
    setCookie(cookieName, cookieId, 730) // 2 years
  }

  return cookieId
}

/**
 * Fallback UUID generation for older browsers
 */
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * Collect device information for fingerprinting
 */
export function getDeviceInfo(): DeviceInfo {
  return {
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    colorDepth: window.screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack || null,
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: (navigator as any).deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints || 0,
    timestamp: new Date().toISOString()
  }
}

/**
 * Generate a device fingerprint hash
 * This creates a unique identifier based on device characteristics
 */
export async function generateDeviceFingerprint(): Promise<string> {
  const deviceInfo = getDeviceInfo()

  // Create fingerprint string from device characteristics
  const fingerprintString = [
    deviceInfo.screenResolution,
    deviceInfo.colorDepth,
    deviceInfo.timezone,
    deviceInfo.language,
    deviceInfo.platform,
    deviceInfo.hardwareConcurrency,
    deviceInfo.deviceMemory || '',
    deviceInfo.maxTouchPoints,
    navigator.userAgent
  ].join('|')

  // Hash the fingerprint string
  if (crypto.subtle) {
    const encoder = new TextEncoder()
    const data = encoder.encode(fingerprintString)
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    return hashHex
  } else {
    // Fallback: simple hash for older browsers
    return simpleHash(fingerprintString)
  }
}

/**
 * Simple hash function fallback for older browsers
 */
function simpleHash(str: string): string {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return Math.abs(hash).toString(16)
}

/**
 * Get complete tracking data for Free SIM order
 */
export async function getTrackingData(): Promise<{
  browser_cookie_id: string
  device_fingerprint: string
  device_info: DeviceInfo
}> {
  const browser_cookie_id = getOrCreateTrackingCookie()
  const device_fingerprint = await generateDeviceFingerprint()
  const device_info = getDeviceInfo()

  return {
    browser_cookie_id,
    device_fingerprint,
    device_info
  }
}
